import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { JPA_QUERY_KEYS, JpaQueries } from '@api/services/jpa';
import { DynamicTableColumn } from '@api/services/jpa/types';
import { useJpa } from '@stores';
import { JpaTableInputOnChangeProps, Snb, JpaTableInputMap } from './tableInput.types';

import {
  JpaTableInputCellData,
  JpaTableInputProps,
  JpaTableInputRowData,
  LastUpdatedData,
} from './jpaTableInput.types';

import { useQuery } from '@tanstack/react-query';
import { Table } from '@trussworks/react-uswds';
import Tooltip from '@components/tooltip/tooltip';
import { mdiDelete as deleteIcon, mdiPlusBox as addIcon, mdiArrowULeftTop as undoIcon } from '@mdi/js';
import { useFormContext } from 'react-hook-form';
import classNames from 'classnames';

import Icon from '@components/icon/Icon';
import Question from '@components/data-driven-form/Question';
import Loader from '@components/loader/Loader';
import { QUESTION_TYPE } from '@components/data-driven-form/ddf.validation';

import _uniqueId from 'lodash/uniqueId';
import _range from 'lodash/range';

import './tableInput.scss';
import Pagination from '../pagination/Pagination';
import FileDownloadList from '../file-download-list/FileDownloadList';

const itemsPerPage = 10;

const getBlankRow = (tableColumnData: DynamicTableColumn[] | undefined, totalRowCount: number) => {
  if (!tableColumnData) {
    return [];
  }

  const row: JpaTableInputRowData = [];

  tableColumnData?.forEach((column) => {
    row.push({
      answerId: 0,
      isDeleted: false,
      questionId: column.jpaQuestionID as number,
      recordId: _uniqueId(),
      rowNumber: totalRowCount + 1,
      typeId: column.jpaColumnType as number,
      value: null,
    });
  });

  return row;
};

const JpaTableInput = ({
  fileDownload,
  formId,
  label: labelText,
  onChange,
  questionId,
  isReadOnly,
  isRequired,
  tooltipText,
  tooltipClickable,
}: JpaTableInputProps) => {
  const addRow = useRef(false);
  const lastUpdated = useRef<LastUpdatedData>({ p1: 0 });
  const [pageNumber, setPageNumber] = useState(1);

  const { clearOrphanedAnswerRows, convertTableAnswerData, formRequestData, tableAddedRowCounts, tableAnswerSet } =
    useJpa();

  const tableData = tableAnswerSet?.[`f${formId}`]?.[`q${questionId}`];
  const addedRowCount = tableAddedRowCounts?.[`f${formId}`]?.[`q${questionId}`] || 0;
  const labelStyles = classNames('usa-label', { isRequired });
  const pageData = tableData?.[`p${pageNumber}`];
  const pageLastUpdated = lastUpdated.current?.[`p${pageNumber}`];
  const requestId = formRequestData?.[formId]?.requestId;
  const tableName = `${QUESTION_TYPE.table}-${questionId}`;
  const version = formRequestData?.[formId]?.version;

  const { setValue } = useFormContext();

  const { data: tableColumnData, isLoading: isLoadingColumnData } = useQuery({
    queryKey: [JPA_QUERY_KEYS.GET_DYNAMIC_TABLE, questionId],
    queryFn: () =>
      JpaQueries.getDynamicTable({
        questionId,
      }),
  });

  const {
    data: tableAnswerData,
    dataUpdatedAt,
    isFetching: isFetchingAnswerData,
  } = useQuery({
    queryKey: [JPA_QUERY_KEYS.GET_DYNAMIC_TABLE_ANSWERS, requestId, version, formId, questionId, pageNumber],
    queryFn: () =>
      JpaQueries.getDynamicTableAnswers({
        formId,
        itemsPerPage,
        pageNumber,
        questionId,
        requestId,
        version,
      }),
    enabled: requestId > 0 && version >= 0 && formId > 0 && questionId > 0,
  });

  const pageKeys = useMemo(() => Object.keys(tableData || {}), [tableData]);

  const tableDataRowCount = useMemo(
    () =>
      pageKeys.reduce((acc: number, pageKey) => {
        const pageData = tableData[pageKey] as JpaTableInputRowData[];
        return acc + (pageData?.length || 0);
      }, 0),
    [pageKeys, tableData]
  );

  const invalidUnloadedRowNumbers = useMemo(() => {
    const loadedRowNumbers = pageKeys.reduce((acc: number[], pageKey) => {
      const pageNumber = Number(pageKey.replace('p', ''));
      const pageStart = (pageNumber - 1) * itemsPerPage + 1;
      const pageRowsNumbers = _range(pageStart, pageStart + itemsPerPage);

      return acc.concat(pageRowsNumbers);
    }, []);

    const invalidRows = tableAnswerData?.invalidRows || [];
    const unloadedPageNumbers = invalidRows.filter((rowNumber) => !loadedRowNumbers.includes(rowNumber));

    return unloadedPageNumbers;
  }, [pageKeys, tableAnswerData?.invalidRows]);

  const invalidLoadedRowNumbers = useMemo(
    () =>
      pageKeys.reduce((pageAcc: number[], pageKey) => {
        const rowNumber = { value: 0 };
        const pageData = tableData[pageKey] as JpaTableInputRowData[];

        if (!pageData) return pageAcc;

        const invalidPageRows = pageData.reduce((rowAcc: number[], row: JpaTableInputRowData) => {
          rowNumber.value += 1;

          if (row[0].isDeleted) {
            return rowAcc;
          }

          const isRowValid = row.every((cell: JpaTableInputCellData, cellIndex: number) =>
            tableColumnData?.[cellIndex]?.isColumnRequired ? cell?.value !== null && cell?.value !== '' : true
          );

          if (isRowValid) {
            return rowAcc;
          }

          const pageNumber = Number(pageKey.replace('p', ''));
          const pageRowNumber = rowNumber.value + (pageNumber - 1) * itemsPerPage;

          return [...rowAcc, pageRowNumber];
        }, []);

        return [...pageAcc, ...invalidPageRows];
      }, []),
    [pageKeys, tableColumnData, tableData]
  );

  const invalidRowNumbers = useMemo(
    () => [...invalidUnloadedRowNumbers, ...invalidLoadedRowNumbers].sort((a, b) => a - b),
    [invalidUnloadedRowNumbers, invalidLoadedRowNumbers]
  );

  const invalidPageNumbers = useMemo(
    () =>
      invalidRowNumbers.reduce((acc: number[], rowNumber) => {
        const pageNumber = Math.floor((rowNumber - 1) / itemsPerPage) + 1;

        if (acc.includes(pageNumber)) {
          return acc;
        }

        acc.push(pageNumber);
        return acc;
      }, []),
    [invalidRowNumbers]
  );

  const totalRowCount = (tableAnswerData?.totalRowCount || 0) + addedRowCount;
  const pageCount = Math.ceil(totalRowCount / itemsPerPage);
  const isDataLoading = isLoadingColumnData || isFetchingAnswerData;
  const isTableValid = invalidRowNumbers.length === 0 && totalRowCount > 0;

  const handleOnChangeAdd = useCallback(() => {
    onChange({
      action: 'add',
      blankRow: getBlankRow(tableColumnData, totalRowCount),
      itemsPerPage,
      totalRowCount,
    });
    addRow.current = false;
  }, [onChange, tableColumnData, totalRowCount]);

  const handleAddRow = useCallback(() => {
    const goToPage = Math.ceil((totalRowCount + 1) / itemsPerPage);

    if (goToPage === pageNumber) {
      handleOnChangeAdd();
    } else {
      addRow.current = true;
    }

    setPageNumber(goToPage);
  }, [handleOnChangeAdd, pageNumber, totalRowCount]);

  const handleDeleteRow = useCallback(
    (pageNumber: number, rowIndex: number) => {
      onChange({
        action: 'delete',
        pageCount,
        pageNumber,
        itemsPerPage,
        rowIndex: rowIndex,
      });
    },
    [onChange, pageCount]
  );

  const handleUndoDeleteRow = useCallback(
    (pageNumber: number, rowIndex: number) => {
      onChange({
        action: 'undo-delete',
        pageNumber,
        itemsPerPage,
        rowIndex: rowIndex,
      });
    },
    [onChange]
  );

  const handleOnChange = useCallback(
    (value: Snb, _questionId: number, _typeId: number, _formId: number, onChangeProps: JpaTableInputOnChangeProps) => {
      onChange({
        action: 'update',
        pageNumber,
        rowIndex: onChangeProps?.rowIndex,
        cellIndex: onChangeProps?.cellIndex,
        value,
      });
    },
    [onChange, pageNumber]
  );

  // add a row if there are no rows in the table
  useEffect(() => {
    if (totalRowCount === 0 && tableDataRowCount === 0 && isDataLoading === false) {
      handleAddRow();
    }
  }, [handleAddRow, isDataLoading, tableDataRowCount, totalRowCount]);

  // convert API data to store format
  useEffect(() => {
    const isNewData = tableAnswerData && tableColumnData && pageLastUpdated !== dataUpdatedAt;

    if (isDataLoading) {
      return;
    }

    if (isNewData) {
      convertTableAnswerData({
        columnData: tableColumnData,
        formId,
        pageNumber,
        questionId,
        tableAnswerData: tableAnswerData.dynamicTableAnswers,
        onComplete: () => {
          lastUpdated.current[`p${pageNumber}`] = dataUpdatedAt;

          if (addRow.current) {
            handleOnChangeAdd();
          } else {
            clearOrphanedAnswerRows(formId, questionId, totalRowCount);

            // if all items on a  page are deleted, go to the last page
            if (tableAnswerData?.dynamicTableAnswers?.length === 0) {
              const lastPageNumber = Math.ceil(totalRowCount / itemsPerPage);
              lastPageNumber !== pageNumber && setPageNumber(lastPageNumber);
            }
          }
        },
      });
    } else if (addRow.current) {
      handleOnChangeAdd();
    }
  }, [
    addRow,
    clearOrphanedAnswerRows,
    convertTableAnswerData,
    dataUpdatedAt,
    formId,
    handleOnChangeAdd,
    isDataLoading,
    lastUpdated,
    pageLastUpdated,
    pageNumber,
    questionId,
    tableAnswerData,
    tableColumnData,
    totalRowCount,
  ]);

  // determine table validation state
  useEffect(() => {
    setValue(tableName, isTableValid ? { rowCount: totalRowCount } : null, { shouldValidate: true });
  }, [isTableValid, setValue, tableName, totalRowCount]);

  if (isDataLoading) {
    return (
      <div>
        <legend className={labelStyles}>
          <span id={`${tableName}_label`}>{labelText}</span>
        </legend>
        <Loader variant='section' />
      </div>
    );
  }

  const addButton = (
    <button className='table-input-button' disabled={isReadOnly} onClick={handleAddRow} type='button'>
      <Icon path={addIcon} size={0.9} /> Add Row
    </button>
  );

  return (
    <fieldset className='table-input-container' id={tableName}>
      <legend className={labelStyles}>
        <span id={`${tableName}_label`}>{labelText}</span>
        {tooltipText && <Tooltip clickable={tooltipClickable} content={tooltipText} header={labelText} name={name} />}
      </legend>
      {fileDownload?.length > 0 && <FileDownloadList files={fileDownload} />}
      <div className='table-scroll-container'>
        {invalidPageNumbers.length > 0 && (
          <div className='table-input-error-message'>
            Incomplete page{invalidPageNumbers.length > 1 ? 's' : ''}: {invalidPageNumbers.join(', ')}
          </div>
        )}
        {totalRowCount} Rows
        <Table bordered>
          <thead>
            <tr>
              <th className='actions-header' />
              {tableColumnData?.map((column: DynamicTableColumn) => (
                <th
                  key={`${column.jpaQuestionID}-${column.jpaDynamicTableID}`}
                  className={column.isColumnRequired ? 'required' : undefined}
                >
                  <span className='table-column-name'>{column.jpaColumnName}</span>
                  {column.jpaColumnHelp && <Tooltip content={column.jpaColumnHelp} header={column.jpaColumnName} />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(pageData as JpaTableInputRowData[])?.map((row: JpaTableInputRowData, rowIndex: number) => {
              const isRowDeleted = row.some((cell: JpaTableInputCellData) => cell?.isDeleted);

              return (
                <tr key={`row-${row[0].recordId}`} className={isRowDeleted ? 'table-row-deleted' : undefined}>
                  <td className='actions-cell'>
                    {isRowDeleted ? (
                      <button
                        className='table-input-button'
                        onClick={() => handleUndoDeleteRow(pageNumber, rowIndex)}
                        title='Undo delete'
                        type='button'
                      >
                        <Icon path={undoIcon} size={0.8} />
                      </button>
                    ) : (
                      <button
                        className='table-input-button'
                        disabled={totalRowCount === 1 || isReadOnly}
                        onClick={() => handleDeleteRow(pageNumber, rowIndex)}
                        title='Delete row'
                        type='button'
                      >
                        <Icon path={deleteIcon} size={0.8} />
                      </button>
                    )}
                  </td>
                  {tableColumnData?.map((column, cellIndex: number) => {
                    const cellData = row[cellIndex];
                    const inputName = `q-${questionId}_rec-${cellData?.recordId}_col-${cellIndex}`;

                    return (
                      <td key={inputName}>
                        <Question
                          defaultValue={cellData?.value !== null ? cellData.value : ''}
                          inputMap={JpaTableInputMap}
                          inputNameOverride={inputName}
                          isReadOnly={isReadOnly || isRowDeleted}
                          onChange={handleOnChange}
                          onChangeProps={{ cellIndex, rowIndex }}
                          questionData={{
                            jpaQuestionID: column.jpaQuestionID,
                            isRequired: column.isColumnRequired,
                            jpaQuestionTypeID: column.jpaColumnType,
                            jpaLists: column.jpaLists,
                          }}
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
        <Pagination
          currentPage={pageNumber}
          itemsPerPage={itemsPerPage}
          onPageChange={setPageNumber}
          pageCount={pageCount}
          pageSizeOptions={[5, 10, 25, 50]}
          rightContent={addButton}
          showPageSizeSelector={false}
        />
      </div>
    </fieldset>
  );
};

export default JpaTableInput;
